import React, { useCallback, useState } from 'react';
import {
  Dropdown,
  DropdownItem,
  DropdownList,
  MenuToggle,
  MenuToggleAction,
  MenuToggleElement,
  Spinner,
} from '@patternfly/react-core';
import { DownloadIcon } from '@patternfly/react-icons';
import { useApiFetch } from '../../../api';
import { useNotifications } from '../../../contexts/NotificationContext';
import type { Certificate } from '../../../types/models/Certificate';
import type { ApiResponseCertificateDownload } from '../../../types/api/ApiResponseCertificate';
import { downloadTextFile, slugifyForFilename } from '../../../utils/fileDownload';

interface CertificateDownloadMenuProps {
  certificate: Certificate;
  // The platform's `support_private_key` capability flag, resolved by the
  // detail page: false while the owning platform is still loading (the key
  // item stays hidden until capability is known), and true when the platform
  // fetch failed so capability can never be known in this view — staff
  // opening another tenant's certificate hit that path by contract (the
  // certificate Show has a staff bypass but the platform Show does not). The
  // key item is then offered anyway and the download endpoint arbitrates: an
  // unsupported platform answers 405 and the JSend message is surfaced.
  privateKeyCapable: boolean;
  // Called when a download error reveals the local row is stale: 409 (status
  // is still `request` locally but differs on the platform) or 404 on
  // include_key (`has_private_key` lags the platform). The parent refetches
  // the certificate so the UI catches up.
  onCertificateStale: () => void;
}

/**
 * Title-bar Download split button for the certificate detail page.
 *
 * The main segment (and the "Certificate (PEM)" item) saves the locally
 * cached leaf PEM. The "Private key (PEM)" item performs one live platform
 * read returning a matching cert+key pair and saves both files — always the
 * `pem` from that same response, never a previously cached one. The key only
 * ever lives in this function's local scope: it is written to the Blob and
 * dropped — never stored in state, storage, or logs.
 */
function CertificateDownloadMenu({ certificate, privateKeyCapable, onCertificateStale }: CertificateDownloadMenuProps) {
  const apiFetch = useApiFetch();
  const { addNotification, setDrawerOpen } = useNotifications();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [isDownloading, setIsDownloading] = useState<boolean>(false);

  const offerPrivateKey = certificate.has_private_key && privateKeyCapable;

  const performDownload = useCallback(
    async (includeKey: boolean) => {
      const fail = (description: string) => {
        addNotification({
          title: includeKey ? 'Could not download the private key' : 'Could not download the certificate',
          srTitle: 'Error alert',
          description,
          variant: 'danger',
        });
        setDrawerOpen(true);
      };

      // Close the menu before the (potentially slow) request: the disabled
      // caret could not close it otherwise, and no second download can be
      // launched from a lingering open menu.
      setIsOpen(false);
      setIsDownloading(true);
      try {
        const response = await apiFetch(
          `/datacenters/certificates/${certificate.id}/download${includeKey ? '?include_key=1' : ''}`,
          // Keep the response (private key material on include_key) out of
          // the browser's on-disk HTTP cache.
          { cache: 'no-store' },
        );
        let body: ApiResponseCertificateDownload | null;
        try {
          body = (await response.json()) as ApiResponseCertificateDownload;
        } catch {
          body = null;
        }

        if (body?.status === 'success' && body.data?.download) {
          const download = body.data.download;
          const stem = slugifyForFilename(download.cn);
          if (!includeKey) {
            downloadTextFile(`${stem}.crt`, download.pem);
            return;
          }
          if (download.private_key === null) {
            // Contract says a missing key is a 404, not a null — defensive.
            onCertificateStale();
            fail('The platform returned no private key for this certificate.');
            return;
          }
          // Two programmatic downloads fire from one gesture and browsers may
          // block the second (Chromium's automatic-downloads guard), so the
          // key — the file this action is about — goes first; the cert PEM is
          // also obtainable via the plain download item. Always save the
          // `pem` of THIS response: both parts come from a single live
          // platform read, so the pair is guaranteed to match — never pair
          // the key with a previously cached PEM.
          downloadTextFile(`${stem}.key`, download.private_key);
          downloadTextFile(`${stem}.crt`, download.pem);
          addNotification({
            title: 'Private key downloaded',
            srTitle: 'Security notice',
            description: `${stem}.key contains the certificate's private key — store it securely and delete any stray copies.`,
            variant: 'info',
          });
          setDrawerOpen(true);
          return;
        }

        const message =
          body?.message ?? `Download failed (HTTP ${`${response.status} ${response.statusText}`.trim()})`;
        // 409: the cert is unissued on the platform while the local row said
        // otherwise. 404 with include_key: the platform stores no key, so the
        // local has_private_key flag is stale. Both mean "refetch the row".
        if (response.status === 409 || (response.status === 404 && includeKey)) {
          onCertificateStale();
        }
        fail(message);
      } catch (err) {
        fail(err instanceof Error ? err.message : 'An unexpected network error occurred.');
      } finally {
        setIsDownloading(false);
      }
    },
    [apiFetch, certificate.id, addNotification, setDrawerOpen, onCertificateStale],
  );

  return (
    <Dropdown
      isOpen={isOpen}
      onSelect={() => setIsOpen(false)}
      onOpenChange={(open: boolean) => setIsOpen(open)}
      shouldFocusToggleOnSelect
      popperProps={{ position: 'right' }}
      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
        <MenuToggle
          ref={toggleRef}
          variant="primary"
          isExpanded={isOpen}
          isDisabled={isDownloading}
          onClick={() => setIsOpen((open) => !open)}
          aria-label="Download format options"
          splitButtonItems={[
            <MenuToggleAction
              id={`certificate-download-action-${certificate.id}`}
              key="download-certificate"
              aria-label="Download certificate PEM"
              isDisabled={isDownloading}
              onClick={() => void performDownload(false)}
            >
              {isDownloading ? (
                <Spinner size="sm" aria-label="Download in progress" />
              ) : (
                <DownloadIcon aria-hidden="true" />
              )}{' '}
              Download
            </MenuToggleAction>,
          ]}
        />
      )}
    >
      <DropdownList>
        <DropdownItem key="certificate" isDisabled={isDownloading} onClick={() => void performDownload(false)}>
          Certificate (PEM)
        </DropdownItem>
        {offerPrivateKey && (
          <DropdownItem
            key="private-key"
            isDisabled={isDownloading}
            description="Fetched live from the platform"
            onClick={() => void performDownload(true)}
          >
            Private key (PEM)
          </DropdownItem>
        )}
      </DropdownList>
    </Dropdown>
  );
}

export default CertificateDownloadMenu;
